import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  RotateCcw,
  ArrowRight,
  Sparkles,
  CheckCircle2,
  XCircle,
  Loader2,
  Link as LinkIcon,
  Download,
  Eye,
  Github,
  BookOpen,
  Cloud,
  Tag,
  ClipboardEdit,
  X,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUS_LABEL = {
  pending_approval: "Pending Approval",
  approved: "Queued",
  transforming: "Transforming…",
  delivered: "Delivered",
  rejected: "Rejected",
  failed: "Failed",
};
const STATUS_COLOR = {
  pending_approval: "text-[color:var(--warning)] border-[color:var(--warning)]/40",
  approved: "text-[color:var(--info)] border-[color:var(--info)]/40",
  transforming: "text-[color:var(--accent)] border-[color:var(--accent)]/40",
  delivered: "text-[color:var(--success)] border-[color:var(--success)]/40",
  rejected: "text-[color:var(--critical)] border-[color:var(--critical)]/40",
  failed: "text-[color:var(--critical)] border-[color:var(--critical)]/40",
};

export default function Transform() {
  const { user } = useAuth();
  const [board, setBoard] = useState({ columns: {}, stages: [], stage_labels: {}, progress_pct: 0, total: 0, live: 0 });
  const [requestSys, setRequestSys] = useState(null);   // system for the request modal
  const [rejectSys, setRejectSys] = useState(null);     // system for the reject modal
  const [artifactSys, setArtifactSys] = useState(null); // system for the artifact modal
  const [diffSys, setDiffSys] = useState(null);         // system for the diff drawer

  const isAdmin = user?.role === "admin";

  const load = async () => {
    const { data } = await api.get("/api/transform/board");
    setBoard(data);
  };
  useEffect(() => { load(); }, []);

  // Poll while any system is transforming
  const anyInFlight = useMemo(() => {
    for (const s of Object.values(board.columns).flat()) {
      const st = s.transformation?.status;
      if (st === "approved" || st === "transforming") return true;
    }
    return false;
  }, [board]);
  useEffect(() => {
    if (!anyInFlight) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [anyInFlight]);

  const advance = async (id) => {
    try {
      await api.post(`/api/transform/board/${id}/advance`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset all cards back to Backlog?")) return;
    try {
      await api.delete("/api/transform/reset");
      await load();
      toast.success("Board reset");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-6 md:p-10 space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">/ stage · 04 — transform</div>
          <h1 className="mt-2 text-3xl md:text-4xl font-medium tracking-tight">
            Migration Execution
          </h1>
          <p className="mt-2 text-[color:var(--text-2)] max-w-3xl">
            Request an AI-driven migration per service, get an admin approval,
            then Revora rewrites the top files against your target stack. Once
            delivered, review a side-by-side diff and hand off the modernized
            code via the attached artifact URLs.
          </p>
        </div>
        <button
          data-testid="reset-transform-btn"
          onClick={reset}
          className="inline-flex items-center gap-2 border border-[color:var(--border-strong)] h-11 px-4 hover:border-[color:var(--critical)] hover:text-[color:var(--critical)] transition-colors duration-150 mono text-xs uppercase tracking-widest"
        >
          <RotateCcw size={14} /> Reset
        </button>
      </div>

      {/* Overall progress */}
      <div className="border border-[color:var(--border)] bg-[color:var(--bg-1)] p-6">
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-lg font-medium tracking-tight">Overall Migration Progress</div>
          <div data-testid="progress-label" className="mono text-xs text-[color:var(--accent)]">
            {board.progress_pct}% complete · {board.live}/{board.total} live
          </div>
        </div>
        <div className="h-2 bg-[color:var(--bg-2)]">
          <div data-testid="progress-fill"
            className="h-2 bg-gradient-to-r from-[color:var(--accent)] to-[color:var(--info)] transition-all duration-500"
            style={{ width: `${board.progress_pct}%` }}
          />
        </div>
      </div>

      {/* Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {board.stages.map((stage) => {
          const cards = board.columns[stage] || [];
          const label = board.stage_labels[stage] || stage;
          return (
            <div key={stage} data-testid={`col-${stage}`}
              className="border border-[color:var(--border)] bg-[color:var(--bg-1)] p-4 min-h-[220px]">
              <div className="flex items-baseline justify-between mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] mb-3">
                <span>{label}</span>
                <span className="text-[color:var(--text-2)]">{cards.length}</span>
              </div>
              <div className="space-y-2">
                {cards.map((s) => (
                  <SystemCard
                    key={s.id}
                    system={s}
                    stage={stage}
                    isAdmin={isAdmin}
                    onAdvance={advance}
                    onRequest={() => setRequestSys(s)}
                    onApprove={async () => {
                      try {
                        await api.post(`/api/transform/board/${s.id}/approve`);
                        toast.success("Approved — transformation started");
                        await load();
                      } catch (e) {
                        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
                      }
                    }}
                    onReject={() => setRejectSys(s)}
                    onArtifact={() => setArtifactSys(s)}
                    onViewDiff={() => setDiffSys(s)}
                  />
                ))}
                {cards.length === 0 && (
                  <div className="mono text-[10px] text-[color:var(--text-3)] p-2">empty</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {requestSys && <RequestModal system={requestSys} onClose={() => setRequestSys(null)} onDone={load} />}
      {rejectSys && <RejectModal system={rejectSys} onClose={() => setRejectSys(null)} onDone={load} />}
      {artifactSys && <ArtifactModal system={artifactSys} onClose={() => setArtifactSys(null)} onDone={load} />}
      {diffSys && <DiffDrawer system={diffSys} onClose={() => setDiffSys(null)} />}
    </div>
  );
}

function SystemCard({ system: s, stage, isAdmin, onAdvance, onRequest, onApprove, onReject, onArtifact, onViewDiff }) {
  const tx = s.transformation;
  const status = tx?.status;
  const hasArtifact = !!(s.artifact_repo_url || s.artifact_docs_url || s.artifact_deploy_url);
  const progressPct = tx && tx.files_total > 0 ? Math.round((tx.files_done / tx.files_total) * 100) : 0;

  return (
    <div data-testid={`card-${s.id}`}
      className="w-full text-left kanban-card border border-[color:var(--border)] bg-[color:var(--bg-2)] p-3 hover:border-[color:var(--accent)]/60 transition-colors duration-150">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{s.name}</div>
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] mt-0.5 truncate">
            {s.tech}
          </div>
        </div>
        {status && (
          <span
            data-testid={`tx-status-${s.id}`}
            className={`mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border shrink-0 ${STATUS_COLOR[status] || ""}`}
          >
            {STATUS_LABEL[status] || status}
          </span>
        )}
      </div>

      {(status === "approved" || status === "transforming") && (
        <div className="mt-3 space-y-1.5">
          <div className="h-1 bg-[color:var(--bg-1)]">
            <div className="h-1 bg-[color:var(--accent)] transition-all duration-500"
              style={{ width: `${progressPct}%` }} />
          </div>
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] flex items-center gap-1.5">
            <Loader2 size={10} className="animate-spin" />
            {tx.files_done}/{tx.files_total} files · {tx.provider_id || "shared"}
          </div>
        </div>
      )}

      {status === "rejected" && tx?.reject_reason && (
        <div className="mt-2 mono text-[10px] text-[color:var(--critical)] border border-[color:var(--critical)]/30 bg-[color:var(--critical)]/5 p-1.5">
          {tx.reject_reason}
        </div>
      )}
      {status === "failed" && tx?.error && (
        <div className="mt-2 mono text-[10px] text-[color:var(--critical)] border border-[color:var(--critical)]/30 bg-[color:var(--critical)]/5 p-1.5 truncate" title={tx.error}>
          {tx.error}
        </div>
      )}

      {hasArtifact && (
        <div className="mt-3 space-y-1">
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">artifact</div>
          {s.artifact_repo_url && (
            <ArtifactLink icon={<Github size={11} />} label="Repo" href={s.artifact_repo_url} testId={`art-repo-${s.id}`} />
          )}
          {s.artifact_docs_url && (
            <ArtifactLink icon={<BookOpen size={11} />} label="Docs" href={s.artifact_docs_url} testId={`art-docs-${s.id}`} />
          )}
          {s.artifact_deploy_url && (
            <ArtifactLink icon={<Cloud size={11} />} label="Deploy" href={s.artifact_deploy_url} testId={`art-deploy-${s.id}`} />
          )}
          {s.artifact_release_version && (
            <div className="mono text-[10px] text-[color:var(--text-2)] flex items-center gap-1.5">
              <Tag size={11} /> v{s.artifact_release_version}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {!status && (
          <CardBtn testId={`request-${s.id}`} onClick={onRequest} icon={<Sparkles size={11} />}>
            Request AI Migration
          </CardBtn>
        )}
        {status === "pending_approval" && isAdmin && (
          <>
            <CardBtn
              testId={`approve-${s.id}`}
              onClick={onApprove}
              icon={<CheckCircle2 size={11} />}
              accent
            >
              Approve
            </CardBtn>
            <CardBtn testId={`reject-${s.id}`} onClick={onReject} icon={<XCircle size={11} />}>
              Reject
            </CardBtn>
          </>
        )}
        {status === "pending_approval" && !isAdmin && (
          <span className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] px-1.5 py-1">
            awaiting admin
          </span>
        )}
        {status === "delivered" && (
          <>
            <CardBtn testId={`view-diff-${s.id}`} onClick={onViewDiff} icon={<Eye size={11} />} accent>
              View Side-by-Side
            </CardBtn>
            <a
              data-testid={`download-${s.id}`}
              href={`${process.env.REACT_APP_BACKEND_URL}/api/transform/board/${s.id}/download`}
              className="inline-flex items-center gap-1.5 border border-[color:var(--border-strong)] px-2 py-1 mono text-[10px] uppercase tracking-widest hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors duration-150"
            >
              <Download size={11} /> Download
            </a>
          </>
        )}
        {(status === "rejected" || status === "failed") && (
          <CardBtn testId={`retry-${s.id}`} onClick={onRequest} icon={<Sparkles size={11} />}>
            Re-request
          </CardBtn>
        )}
        {isAdmin && (
          <CardBtn testId={`artifact-${s.id}`} onClick={onArtifact} icon={<ClipboardEdit size={11} />}>
            {hasArtifact ? "Edit Artifact" : "Attach Artifact"}
          </CardBtn>
        )}
        {stage !== "live_in_cloud" && (
          <button
            data-testid={`advance-${s.id}`}
            onClick={() => onAdvance(s.id)}
            className="ml-auto mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] hover:text-[color:var(--accent)] flex items-center gap-1"
            title="Advance to next column"
          >
            advance <ArrowRight size={10} />
          </button>
        )}
      </div>
    </div>
  );
}

function ArtifactLink({ icon, label, href, testId }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid={testId}
      className="mono text-[10px] text-[color:var(--text-2)] hover:text-[color:var(--accent)] flex items-center gap-1.5 truncate">
      {icon}
      <span className="uppercase tracking-widest">{label}</span>
      <span className="truncate text-[color:var(--text-3)]">· {href.replace(/^https?:\/\//, "")}</span>
    </a>
  );
}

function CardBtn({ children, onClick, icon, accent, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 border px-2 py-1 mono text-[10px] uppercase tracking-widest transition-colors duration-150 ${
        accent
          ? "border-[color:var(--accent)] bg-[color:var(--accent)]/10 text-[color:var(--accent)] hover:bg-[color:var(--accent)]/20"
          : "border-[color:var(--border-strong)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

// ---------------- Modals ----------------

function ModalShell({ title, onClose, children, testId }) {
  return (
    <div data-testid={testId} className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg border border-[color:var(--border)] bg-[color:var(--bg-1)]">
        <div className="border-b border-[color:var(--border)] px-5 py-3 flex items-center justify-between">
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--accent)]">{title}</div>
          <button onClick={onClose} className="text-[color:var(--text-3)] hover:text-white" data-testid="modal-close">
            <X size={14} />
          </button>
        </div>
        <div className="p-5 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function RequestModal({ system, onClose, onDone }) {
  const STACK_OPTIONS = [
    { id: "java-spring", name: "Java · Spring Boot" },
    { id: "dotnet-core", name: ".NET Core" },
    { id: "node-nestjs", name: "Node · NestJS" },
  ];
  
  const [selectedStackId, setSelectedStackId] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch the workspace's selected stack on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/prescribe/stacks");
        const prescribeSelected = data.selected;
        // Default to Prescribe selection, fallback to first option
        setSelectedStackId(prescribeSelected || STACK_OPTIONS[0].id);
      } catch (e) {
        // Fallback to first option if API fails
        setSelectedStackId(STACK_OPTIONS[0].id);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const selectedStack = STACK_OPTIONS.find(s => s.id === selectedStackId);
      await api.post(`/api/transform/board/${system.id}/request`, {
        stack_id: selectedStackId,
        target_stack: selectedStack?.name || "cloud-native",
        notes: notes.trim() || null,
      });
      toast.success("Transformation requested — awaiting admin approval");
      onDone();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title={`/ request migration · ${system.name}`} onClose={onClose} testId="request-modal">
      <div className="mono text-xs text-[color:var(--text-2)]">
        Revora will use your workspace's active AI provider to migrate this
        system's top files. Admin approval required before any tokens are spent.
      </div>
      
      <label className="block">
        <span className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">Target stack</span>
        {loading ? (
          <div className="mt-1 w-full h-10 bg-[color:var(--bg-base)] border border-[color:var(--border)] px-3 flex items-center">
            <Loader2 size={14} className="animate-spin text-[color:var(--text-3)]" />
            <span className="ml-2 mono text-sm text-[color:var(--text-3)]">Loading...</span>
          </div>
        ) : (
          <Select value={selectedStackId} onValueChange={setSelectedStackId}>
            <SelectTrigger 
              data-testid="request-stack-select"
              className="mt-1 w-full h-10 bg-[color:var(--bg-base)] border border-[color:var(--border)] px-3 mono text-sm text-white focus:border-[color:var(--accent)] focus:outline-none focus:ring-1 focus:ring-[color:var(--accent)]"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[color:var(--bg-1)] border border-[color:var(--border)]">
              {STACK_OPTIONS.map((stack) => (
                <SelectItem 
                  key={stack.id} 
                  value={stack.id}
                  data-testid={`stack-option-${stack.id}`}
                  className="mono text-sm text-white hover:bg-[color:var(--bg-2)] focus:bg-[color:var(--bg-2)] cursor-pointer"
                >
                  {stack.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </label>

      <LabelInput label="Notes (optional)" value={notes} onChange={setNotes} testId="request-notes" placeholder="Deliverables, constraints, cutover window…" />
      <div className="flex justify-end gap-2 pt-2">
        <BtnGhost onClick={onClose}>Cancel</BtnGhost>
        <BtnAccent testId="request-submit" onClick={submit} disabled={busy || loading || !selectedStackId}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Submit for Approval
        </BtnAccent>
      </div>
    </ModalShell>
  );
}

function RejectModal({ system, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/api/transform/board/${system.id}/reject`, { reason: reason.trim() });
      toast.success("Rejected");
      onDone();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  return (
    <ModalShell title={`/ reject request · ${system.name}`} onClose={onClose} testId="reject-modal">
      <LabelInput label="Reason (required)" value={reason} onChange={setReason}
        testId="reject-reason" placeholder="Why is this being rejected?" />
      <div className="flex justify-end gap-2 pt-2">
        <BtnGhost onClick={onClose}>Cancel</BtnGhost>
        <button
          data-testid="reject-submit"
          onClick={submit}
          disabled={busy || reason.trim().length < 3}
          className="inline-flex items-center gap-2 border border-[color:var(--critical)] text-[color:var(--critical)] h-10 px-4 hover:bg-[color:var(--critical)]/10 mono text-xs uppercase tracking-widest disabled:opacity-40"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />} Reject
        </button>
      </div>
    </ModalShell>
  );
}

function ArtifactModal({ system, onClose, onDone }) {
  const [repo, setRepo] = useState(system.artifact_repo_url || "");
  const [docs, setDocs] = useState(system.artifact_docs_url || "");
  const [deploy, setDeploy] = useState(system.artifact_deploy_url || "");
  const [version, setVersion] = useState(system.artifact_release_version || "");
  const [notes, setNotes] = useState(system.artifact_notes || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/api/transform/board/${system.id}/artifact`, {
        repo_url: repo, docs_url: docs, deploy_url: deploy,
        release_version: version, notes,
      });
      toast.success("Artifact updated");
      onDone();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  const clear = async () => {
    if (!window.confirm("Clear all artifact URLs for this system?")) return;
    setBusy(true);
    try {
      await api.delete(`/api/transform/board/${system.id}/artifact`);
      toast.success("Artifact cleared");
      onDone();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  return (
    <ModalShell title={`/ modernized artifact · ${system.name}`} onClose={onClose} testId="artifact-modal">
      <div className="mono text-xs text-[color:var(--text-2)]">
        Where can the client find the modernized code, docs, and deployment
        after handoff? Only admins can edit these.
      </div>
      <LabelInput label="Modernized Repo URL" value={repo} onChange={setRepo}
        testId="artifact-repo" placeholder="https://github.com/ivorovi/acme-modernized" />
      <LabelInput label="Docs URL" value={docs} onChange={setDocs}
        testId="artifact-docs" placeholder="https://docs.acme.com/modernized" />
      <LabelInput label="Deployment URL" value={deploy} onChange={setDeploy}
        testId="artifact-deploy" placeholder="https://acme-modernized.azurewebsites.net" />
      <LabelInput label="Release Version" value={version} onChange={setVersion}
        testId="artifact-version" placeholder="1.0.0" />
      <LabelInput label="Notes" value={notes} onChange={setNotes}
        testId="artifact-notes" placeholder="Handoff notes for the client" />
      <div className="flex justify-between gap-2 pt-2">
        <button data-testid="artifact-clear" onClick={clear} disabled={busy}
          className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] hover:text-[color:var(--critical)]">
          clear all
        </button>
        <div className="flex gap-2">
          <BtnGhost onClick={onClose}>Cancel</BtnGhost>
          <BtnAccent testId="artifact-save" onClick={save} disabled={busy}>
            {busy ? <Loader2 size={12} className="animate-spin" /> : <LinkIcon size={12} />} Save
          </BtnAccent>
        </div>
      </div>
    </ModalShell>
  );
}

function DiffDrawer({ system, onClose }) {
  const [state, setState] = useState({ loading: true, job: null, files: [], active: 0 });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/api/transform/board/${system.id}/diff`);
        if (alive) setState({ loading: false, job: data.job, files: data.files, active: 0 });
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
        onClose();
      }
    })();
    return () => { alive = false; };
  }, [system.id]);

  const file = state.files[state.active];

  return (
    <div data-testid="diff-drawer" className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex flex-col">
      <div className="border-b border-[color:var(--border)] bg-[color:var(--bg-1)] px-5 py-3 flex items-center justify-between gap-4">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--accent)]">/ side-by-side · {system.name}</div>
          <div className="text-sm text-[color:var(--text-2)] mt-0.5 flex items-center gap-3 mono">
            <ShieldCheck size={12} className="text-[color:var(--success)]" />
            {state.job?.provider_id || "shared"} · {state.job?.target_stack} · delivered {state.job?.delivered_at ? new Date(state.job.delivered_at).toLocaleString() : "—"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a
            data-testid="diff-download"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/transform/board/${system.id}/download`}
            className="inline-flex items-center gap-1.5 border border-[color:var(--border-strong)] h-9 px-3 mono text-[10px] uppercase tracking-widest hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]"
          >
            <Download size={12} /> Download ZIP
          </a>
          <button data-testid="diff-close" onClick={onClose}
            className="border border-[color:var(--border-strong)] h-9 w-9 flex items-center justify-center hover:text-[color:var(--critical)] hover:border-[color:var(--critical)]">
            <X size={14} />
          </button>
        </div>
      </div>

      {state.loading ? (
        <div className="flex-1 flex items-center justify-center mono text-xs text-[color:var(--text-3)]">
          <Loader2 size={14} className="animate-spin mr-2" /> loading migration bundle…
        </div>
      ) : state.files.length === 0 ? (
        <div className="flex-1 flex items-center justify-center mono text-xs text-[color:var(--text-3)]">
          No files in this transformation.
        </div>
      ) : (
        <div className="flex-1 min-h-0 grid grid-cols-[240px_1fr]">
          {/* File list */}
          <div className="border-r border-[color:var(--border)] bg-[color:var(--bg-1)] overflow-y-auto">
            {state.files.map((f, i) => (
              <button
                key={f.path}
                data-testid={`diff-file-${i}`}
                onClick={() => setState((s) => ({ ...s, active: i }))}
                className={`w-full text-left px-3 py-2 border-b border-[color:var(--border)] mono text-[11px] truncate ${
                  i === state.active
                    ? "bg-[color:var(--bg-2)] text-white border-l-2 border-l-[color:var(--accent)]"
                    : "text-[color:var(--text-2)] hover:bg-[color:var(--bg-2)]"
                } ${f.status === "failed" ? "text-[color:var(--critical)]" : ""}`}
                title={f.path}
              >
                {f.path}
                {f.status === "failed" && <span className="ml-1 text-[9px]">· failed</span>}
              </button>
            ))}
          </div>
          {/* Side-by-side */}
          <div className="grid grid-cols-2 gap-px bg-[color:var(--border)] overflow-hidden">
            <SidePane
              title="original"
              subtitle={file?.path}
              content={file?.original || ""}
              testId="diff-pane-original"
            />
            <SidePane
              title="modernized"
              subtitle={state.job?.target_stack || ""}
              content={file?.transformed || file?.error || "(empty)"}
              accent
              testId="diff-pane-transformed"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function SidePane({ title, subtitle, content, accent, testId }) {
  return (
    <div className="flex flex-col min-h-0 bg-[color:var(--bg-base)]">
      <div className={`px-3 py-2 border-b border-[color:var(--border)] flex items-center gap-2 ${
        accent ? "bg-[color:var(--accent)]/5" : "bg-[color:var(--bg-1)]"
      }`}>
        <span className={`mono text-[10px] uppercase tracking-widest ${accent ? "text-[color:var(--accent)]" : "text-[color:var(--text-2)]"}`}>
          {title}
        </span>
        <span className="mono text-[10px] text-[color:var(--text-3)] truncate">· {subtitle}</span>
      </div>
      <pre
        data-testid={testId}
        className="flex-1 min-h-0 overflow-auto text-[12px] leading-[1.5] p-3 mono text-[color:var(--text-2)] whitespace-pre"
      >
{content}
      </pre>
    </div>
  );
}

function LabelInput({ label, value, onChange, testId, placeholder }) {
  return (
    <label className="block">
      <span className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">{label}</span>
      <input
        data-testid={testId}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full h-10 bg-[color:var(--bg-base)] border border-[color:var(--border)] px-3 mono text-sm text-white placeholder:text-[color:var(--text-3)] focus:border-[color:var(--accent)] focus:outline-none focus:ring-1 focus:ring-[color:var(--accent)]"
      />
    </label>
  );
}

function BtnAccent({ children, onClick, disabled, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} disabled={disabled}
      className="inline-flex items-center gap-2 bg-[color:var(--accent)] text-black h-10 px-4 hover:bg-[color:var(--accent-hover)] mono text-xs uppercase tracking-widest disabled:opacity-40">
      {children}
    </button>
  );
}

function BtnGhost({ children, onClick }) {
  return (
    <button onClick={onClick}
      className="inline-flex items-center gap-2 border border-[color:var(--border-strong)] h-10 px-4 hover:border-white hover:text-white mono text-xs uppercase tracking-widest">
      {children}
    </button>
  );
}
